import React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '@/components/ui-v2/sidebar';
import { StreamingCount } from '@/components/ui-v2/streaming-count';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import {
  DealItem,
  DealItemGenericPropertyInput,
  DealItemUpdateInput,
  PricingMode,
} from '@/lib/graphql/generated/graphql';
import { useCountryNames } from '@/lib/hooks/useCountryNames';
import { useExecuteOptimisticUpdate } from '@/lib/hooks/useExecuteOptimisticUpdate';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { trackEvent } from '@/lib/mixpanel';
import { cn, formatDecimal, getVatLabelKey } from '@/lib/utils';
import { shouldHideBrandPricingColumns } from '@/lib/utils/column-visibility';
import { computeProductGroupKey } from '@/lib/utils/product-grouping';
import {
  areAllShippingFeesZero,
  shouldHideShippingPolicyDialog,
} from '@/lib/utils/shipping-policy-utils';
import {
  isDealItemCategoryUpdating,
  isDealItemVatRateUpdating,
} from '@/lib/utils/updating-fields';
import { FR_VAT_RATES, isSameVatRate } from '@/lib/utils/vat-rates';
import { dealIdentifierState } from '@/states/deal-identifier.state';
import { filteredRowsCountState } from '@/states/filtered-flat-rows.state';
import { pricingModeState } from '@/states/pricing-mode.state';
import { productsState } from '@/states/products.state';
import {
  selectedProductIdsState,
  selectedProductsCountState,
  useResetSelection,
} from '@/states/row-selection.state';
import { shippingPolicyState } from '@/states/shipping-policy.state';
import { storeAtom } from '@/states/store.state';
import { Currency } from '@/types/currency';
import { ShippingMode } from '@/types/shipping';
import { Store } from '@/types/store';
import { getDefaultStore, useAtomValue } from 'jotai';
import { AlertTriangle, ChevronsUpDown } from 'lucide-react';

import { CountryList } from './country-list';
import ShippingPolicyDialog from './ui-v2/shipping-policy/shipping-policy-dialog';
import { Checkbox } from './ui/checkbox';

const confirmDialogFieldLabels: Record<string, string> = {
  name: 'kpi.name',
  chooseDiscount: 'kpi.choose_discount',
  choosePrice: 'kpi.choose_price',
  discountedPrice: 'kpi.discounted_price',
  buyingPrice: 'kpi.buying_price',
  listPrice: 'kpi.list_price',
  marginPercentage: 'kpi.margin_percentage',
  shippingPrice: 'kpi.shipping_price',
  madeIn: 'kpi.made_in',
  stockQuantity: 'kpi.stock',
  vatRate: 'kpi.vat',
  hasVatMargin: 'kpi.vat_ms_long',
  eeePrice: 'kpi.eee_price',
  gtin: 'kpi.gtin',
};

export interface BulkEditSidebarProps {
  onClose: () => void;
}

const BulkEditSidebar: React.FC<BulkEditSidebarProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const dealId = useAtomValue(dealIdentifierState);
  const store = useAtomValue(storeAtom);
  const vatLabel = t(getVatLabelKey(store));
  const user = useUser();
  const products = useAtomValue(productsState);

  const selectedProductIds = useAtomValue(selectedProductIdsState);
  const totalCount = useAtomValue(filteredRowsCountState);

  const { resetSelection } = useResetSelection();
  const { updateProducts } = useUpdateDealItems(dealId);
  const country = useCountryNames();
  const { executeOptimisticUpdate } = useExecuteOptimisticUpdate();

  // Internal state
  const [countryPopoverOpen, setCountryPopoverOpen] = useState(false);
  const [vatRatePopoverOpen, setVatRatePopoverOpen] = useState(false);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [hasVatMargin, setHasVatMargin] = useState<boolean>(false);
  const [showShippingPolicyModal, setShowShippingPolicyModal] = useState(false);
  const [variantConfirmOpen, setVariantConfirmOpen] = useState(false);
  const [isAddingVariant, setIsAddingVariant] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    field: keyof DealItem | null;
    value: string;
    validationFn?: (value: number) => boolean;
  }>({
    open: false,
    field: null,
    value: '',
  });

  // Computed values
  const isBrand = !user?.isInternalUser;
  // Masquer discount choose / prix choose final aux Marques sur les offres ciblées,
  // en cohérence avec la visibilité des colonnes du tableau.
  const hideBrandPricing = shouldHideBrandPricingColumns(
    !!user?.isInternalUser,
    dealId
  );

  const hasFieldLabel =
    !!confirmDialog.field && !!confirmDialogFieldLabels[confirmDialog.field];

  // Compute counts (use derived atoms)
  const selectedCount = useAtomValue(selectedProductsCountState);
  const pricingMode = useAtomValue(pricingModeState);
  const isBuyingPriceFixedMode = pricingMode === PricingMode.BuyingPriceFixed;
  const isMarginFixedMode = pricingMode === PricingMode.MarginFixed;

  // After a successful bulk edit: reset selection then close
  const closeAndResetSelection = () => {
    resetSelection();
    onClose();
  };

  // Handle bulk edit field (moved from product-view)
  const handleBulkEditField = async (
    field: keyof DealItem,
    value: DealItem[keyof DealItem]
  ): Promise<boolean> => {
    const selectedProducts = products.filter((product) =>
      selectedProductIds.includes(product.id)
    );

    if (selectedProducts.length === 0) {
      toast({
        title: t('general.error_no_product_selected'),
        description: t('general.error_please_select_products'),
        variant: 'destructive',
      });
      return false;
    }

    // Same scope as the rate picker in the table: an item on VAT on margin has
    // no editable rate, and one whose rate or category is being updated
    // server-side would race that update.
    const targetProducts =
      field === 'vatRate'
        ? selectedProducts.filter(
            (product) =>
              !product.activeVersion.hasVatMargin &&
              !isDealItemCategoryUpdating(product) &&
              !isDealItemVatRateUpdating(product)
          )
        : selectedProducts;

    if (targetProducts.length === 0) {
      toast({
        title: t('general.error'),
        description: t('general.error_no_vat_rate_editable_product'),
        variant: 'destructive',
      });
      return false;
    }

    const skippedCount = selectedProducts.length - targetProducts.length;
    if (skippedCount > 0) {
      toast({
        title: t('general.vat_rate_items_skipped', { count: skippedCount }),
      });
    }

    const itemsToSave: DealItemUpdateInput[] = targetProducts.map(
      (product) => ({
        id: product.id,
        [field]: value,
      })
    );

    const selectedProductsIds = new Set(targetProducts.map((u) => u.id));
    return executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((p) => {
          if (!selectedProductsIds.has(p.id)) return p;
          const nextVersion = { ...p.activeVersion, [field]: value };
          return {
            ...p,
            activeVersion: {
              ...nextVersion,
              productGroupKey: computeProductGroupKey(
                nextVersion.name,
                nextVersion.isSelected
              ),
            },
          };
        }),
      mutation: () => updateProducts(itemsToSave),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_modifications'),
      defaultErrorMessage: t('general.error_cannot_save_modifications'),
      errorContext: 'handleBulkEditField',
      itemIds: [...selectedProductsIds],
      inProgressMessage: t('general.modifying_products_in_progress'),
      onSuccess: () => {
        trackEvent('BULK_EDIT_APPLIED', {
          dealId,
          fieldName: String(field),
          affectedCount: targetProducts.length,
        });
        const successToast = toast({
          title: t('general.success'),
          description: t('general.success_modified_products'),
          variant: 'default',
        });
        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          successToast.dismiss();
        }, 5000);
      },
      onError: (errorMessage) => {
        trackEvent('BULK_EDIT_FAILED', {
          dealId,
          fieldName: String(field),
          affectedCount: targetProducts.length,
          errorMessage,
        });
      },
    });
  };

  const handleBulkAddVariantOption = async (): Promise<boolean> => {
    const optionName = inputs.variantOptionName?.trim();
    const optionValue = inputs.variantOptionValue?.trim() ?? '';
    const isDeletingOption = optionValue === '';
    if (!optionName || isAddingVariant) return false;

    const selectedIds = new Set(selectedProductIds);
    const selectedProducts = products.filter((product) =>
      selectedIds.has(product.id)
    );

    if (selectedProducts.length === 0) {
      toast({
        title: t('general.error_no_product_selected'),
        description: t('general.error_please_select_products'),
        variant: 'destructive',
      });
      return false;
    }

    const mergeOption = (
      options: DealItem['options']
    ): DealItemGenericPropertyInput[] => {
      const normalizedName = optionName.toLocaleLowerCase();
      let replaced = false;
      const merged: DealItemGenericPropertyInput[] = [];

      for (const option of options ?? []) {
        if (option.name.trim().toLocaleLowerCase() === normalizedName) {
          if (!isDeletingOption && !replaced) {
            merged.push({ name: optionName, value: optionValue });
          }
          replaced = true;
        } else {
          merged.push({ name: option.name, value: option.value });
        }
      }

      if (!isDeletingOption && !replaced) {
        merged.push({ name: optionName, value: optionValue });
      }
      return merged;
    };

    const itemsToSave: DealItemUpdateInput[] = selectedProducts.map(
      (product) => ({
        id: product.id,
        options: mergeOption(product.activeVersion.options),
      })
    );
    const optionsById = new Map(
      itemsToSave.map((item) => [item.id, item.options ?? []])
    );

    setIsAddingVariant(true);
    try {
      return await executeOptimisticUpdate({
        optimisticUpdate: (prev) =>
          prev.map((product) => {
            const options = optionsById.get(product.id);
            if (!options) return product;
            return {
              ...product,
              activeVersion: { ...product.activeVersion, options },
            };
          }),
        mutation: () => updateProducts(itemsToSave),
        checkSuccess: (result) =>
          result.data?.updateDealItemsV2.__typename ===
          'MutationUpdateDealItemsSuccess',
        getErrorMessage: (result) =>
          result.data?.updateDealItemsV2.__typename === 'DomainException'
            ? result.data.updateDealItemsV2.code
            : t('general.error_cannot_save_modifications'),
        defaultErrorMessage: t('general.error_cannot_save_modifications'),
        errorContext: 'handleBulkAddVariantOption',
        inProgressMessage: t('general.modifying_products_in_progress'),
        itemIds: selectedProducts.map((product) => product.id),
        modifiedFields: ['options'],
        onSuccess: () => {
          trackEvent('BULK_EDIT_APPLIED', {
            dealId,
            fieldName: 'options',
            affectedCount: selectedProducts.length,
          });
          toast({
            title: t('general.success'),
            description: t(
              isDeletingOption
                ? 'components.bulk_edit_sidebar.variant_option_delete_success'
                : 'components.bulk_edit_sidebar.variant_option_success',
              { count: selectedProducts.length }
            ),
          });
        },
        onError: (errorMessage) => {
          trackEvent('BULK_EDIT_FAILED', {
            dealId,
            fieldName: 'options',
            affectedCount: selectedProducts.length,
            errorMessage,
          });
        },
      });
    } finally {
      setIsAddingVariant(false);
    }
  };

  const handleConfirmedVariantOption = async () => {
    const success = await handleBulkAddVariantOption();
    setVariantConfirmOpen(false);
    if (!success) return;

    setInputs((prev) => ({
      ...prev,
      variantOptionName: '',
      variantOptionValue: '',
    }));
    closeAndResetSelection();
  };

  const openConfirmDialog = (
    field: keyof DealItem,
    value: string,
    validationFn?: (value: number) => boolean
  ) => {
    setConfirmDialog({
      open: true,
      field,
      value,
      validationFn,
    });
  };

  const handleShippingPolicyEdit = () => {
    const store = getDefaultStore();
    const updatedProducts = store.get(productsState);
    const currentShippingPolicy = store.get(shippingPolicyState);
    const allShippingFeesZero = areAllShippingFeesZero(updatedProducts);

    const shippingPolicyWithUpdatedFees = {
      ...currentShippingPolicy,
      allShippingFeesZero,
    };

    const hasShippingPolicy = shouldHideShippingPolicyDialog({
      shippingPolicy: shippingPolicyWithUpdatedFees,
      mode: 'edit',
    });

    if (!hasShippingPolicy) {
      setShowShippingPolicyModal(true);
      return;
    }

    closeAndResetSelection();
    return;
  };

  // Generic handler for all field types. Returns true when the edit succeeded.
  const handleFieldEdit = async (
    field: keyof DealItem,
    value: string,
    validationFn?: (value: number) => boolean
  ): Promise<boolean> => {
    let processedValue: DealItem[keyof DealItem] =
      null as DealItem[keyof DealItem];

    if (value.trim()) {
      processedValue = value.trim();
    }

    if (validationFn && value.trim()) {
      const numericValue = parseFloat(value.replace(',', '.'));
      if (!validationFn(numericValue)) {
        toast({
          title: t('general.error'),
          description: t('general.error_non_authorized_value'),
          variant: 'destructive',
        });
        return false;
      }
      processedValue = numericValue;
    }

    if (field === 'hasVatMargin' && value.trim()) {
      processedValue = (value === 'true') as DealItem[keyof DealItem];
    }

    const success = await handleBulkEditField(
      field as keyof DealItem,
      processedValue
    );
    if (success) {
      setInputs((prev) => ({ ...prev, [field]: '' }));
    }
    return success;
  };

  const handleConfirmedEdit = async () => {
    if (!confirmDialog.field) return;

    const field = confirmDialog.field;
    const success = await handleFieldEdit(
      field,
      confirmDialog.value,
      confirmDialog.validationFn
    );

    setConfirmDialog({ open: false, field: null, value: '' });

    if (field === 'shippingPrice' && success) {
      handleShippingPolicyEdit();
    } else if (success) {
      closeAndResetSelection();
    }
  };

  const updateHasVatMargin = (value: boolean) => {
    setHasVatMargin(value);
  };

  const updateInput = (field: string, value: string) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const roundChoosePriceToTenth = async (direction: 'ceil' | 'floor') => {
    const selectedProducts = products.filter((product) =>
      selectedProductIds.includes(product.id)
    );

    const itemsToSave: DealItemUpdateInput[] = selectedProducts
      .filter(
        (product) => typeof product.activeVersion.choosePrice === 'number'
      )
      .map((product) => {
        const currentChoosePrice = product.activeVersion.choosePrice as number;
        const roundedChoosePrice =
          direction === 'ceil'
            ? Math.ceil(currentChoosePrice * 10) / 10
            : Math.floor(currentChoosePrice * 10) / 10;

        return {
          id: product.id,
          choosePrice: roundedChoosePrice,
        };
      });

    if (itemsToSave.length === 0) {
      toast({
        title: t('general.error'),
        description: t('general.error_cannot_save_modifications'),
        variant: 'destructive',
      });
      return;
    }

    const selectedProductIdsSet = new Set(itemsToSave.map(({ id }) => id));
    const itemsToSaveMap = new Map(itemsToSave.map((item) => [item.id, item]));

    const success = await executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((product) => {
          const itemToSave = itemsToSaveMap.get(product.id);
          if (!itemToSave) return product;

          return {
            ...product,
            activeVersion: {
              ...product.activeVersion,
              choosePrice: itemToSave.choosePrice ?? null,
            },
          };
        }),
      mutation: () => updateProducts(itemsToSave),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_modifications'),
      defaultErrorMessage: t('general.error_cannot_save_modifications'),
      errorContext: 'roundChoosePriceToTenth',
      inProgressMessage: t('general.modifying_products_in_progress'),
      itemIds: Array.from(selectedProductIdsSet),
      modifiedFields: ['choosePrice'],
      onSuccess: () => {
        trackEvent('BULK_EDIT_APPLIED', {
          dealId,
          fieldName: 'choosePrice',
          affectedCount: itemsToSave.length,
        });
        const successToast = toast({
          title: t('general.success'),
          description: t('general.success_modified_products'),
          variant: 'default',
        });
        setTimeout(() => {
          successToast.dismiss();
        }, 5000);
      },
      onSuccessClose: closeAndResetSelection,
      onError: (errorMessage) => {
        trackEvent('BULK_EDIT_FAILED', {
          dealId,
          fieldName: 'choosePrice',
          affectedCount: itemsToSave.length,
          errorMessage,
        });
      },
    });

    if (success) {
      setInputs((prev) => ({ ...prev, choosePrice: '' }));
    }
  };

  return (
    <>
      <Sidebar
        title={t('components.bulk_edit_sidebar.title')}
        onClose={onClose}
        zIndexClass="z-bulk-edit-sidebar"
      >
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-medium">
            {t('general.products_selected')} :{' '}
          </span>
          <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
            {selectedCount} /{' '}
            <StreamingCount
              count={totalCount}
              className="inline align-text-bottom"
            />
          </span>
        </div>

        <div className="space-y-8">
          {/* Section Infos Produits */}
          <div className="space-y-4">
            <h4 className="border-b border-gray-200 pb-1 text-sm font-medium text-gray-700">
              {t('components.bulk_edit_sidebar.products_info')}
            </h4>

            <div className="ml-2 space-y-4">
              {/* Nom du produit */}
              <div className="space-y-2">
                <Label>{t('kpi.name')}</Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.name || ''}
                    onChange={(e) => updateInput('name', e.target.value)}
                    type="text"
                    placeholder={t('kpi.name')}
                    className="flex-1"
                  />
                  <Button
                    onClick={() => openConfirmDialog('name', inputs.name || '')}
                    size="sm"
                    disabled={!inputs.name?.trim()}
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Add, replace, or delete a variant option on every selected product */}
              <div className="space-y-2">
                <Label>
                  {t('components.bulk_edit_sidebar.variant_option')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('components.bulk_edit_sidebar.variant_option_description')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={inputs.variantOptionName || ''}
                    onChange={(e) =>
                      updateInput('variantOptionName', e.target.value)
                    }
                    type="text"
                    className="h-9"
                    placeholder={t(
                      'components.edit_variant_dialog.option_name_placeholder'
                    )}
                    aria-label={t(
                      'components.edit_variant_dialog.option_name_label'
                    )}
                    data-testid="bulk-variant-option-name"
                  />
                  <Input
                    value={inputs.variantOptionValue || ''}
                    onChange={(e) =>
                      updateInput('variantOptionValue', e.target.value)
                    }
                    type="text"
                    className="h-9"
                    placeholder={t(
                      'components.edit_variant_dialog.option_value_placeholder'
                    )}
                    aria-label={t(
                      'components.edit_variant_dialog.option_value_label'
                    )}
                    data-testid="bulk-variant-option-value"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setVariantConfirmOpen(true)}
                  disabled={
                    !inputs.variantOptionName?.trim() || isAddingVariant
                  }
                  data-testid="bulk-add-variant-option"
                >
                  {t(
                    inputs.variantOptionValue?.trim()
                      ? 'components.bulk_edit_sidebar.add_variant_option'
                      : 'components.bulk_edit_sidebar.delete_variant_option'
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Section Prix */}
          <div className="space-y-4">
            <h4 className="border-b border-gray-200 pb-1 text-sm font-medium text-gray-700">
              {t('components.bulk_edit_sidebar.prices')}
            </h4>

            <div className="ml-2 space-y-4">
              {/* Prix Public TTC*/}
              <div className="space-y-2">
                <Label>
                  {t('kpi.list_price')} ({vatLabel}) (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.listPrice || ''}
                    onChange={(e) => updateInput('listPrice', e.target.value)}
                    type="text"
                    placeholder="ex: 24.99"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'listPrice',
                        inputs.listPrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Discount Choose */}
              {!hideBrandPricing && (
                <div className="space-y-2">
                  <Label>{t('kpi.choose_discount')} (%)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputs.chooseDiscount || ''}
                      onChange={(e) =>
                        updateInput('chooseDiscount', e.target.value)
                      }
                      type="text"
                      placeholder="ex: 25"
                      className="flex-1"
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'chooseDiscount',
                          inputs.chooseDiscount || '',
                          (value) => value >= 0 && value <= 100
                        )
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Prix Choose Final */}
              {!hideBrandPricing && (
                <div className="space-y-2">
                  <Label>
                    {t('kpi.choose_price')} ({vatLabel}) (
                    {store === Store.FR ? Currency.EUR : Currency.USD})
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputs.choosePrice || ''}
                      onChange={(e) =>
                        updateInput('choosePrice', e.target.value)
                      }
                      type="text"
                      placeholder="ex: 24.99"
                      className="flex-1"
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'choosePrice',
                          inputs.choosePrice || '',
                          (value) => value >= 0
                        )
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void roundChoosePriceToTenth('floor')}
                      disabled={selectedCount === 0}
                      className="w-full"
                    >
                      {t(
                        'components.bulk_edit_sidebar.round_choose_price_down'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void roundChoosePriceToTenth('ceil')}
                      disabled={selectedCount === 0}
                      className="w-full"
                    >
                      {t('components.bulk_edit_sidebar.round_choose_price_up')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Marge Choose */}
              {!isBrand && isMarginFixedMode && (
                <div className="space-y-2">
                  <Label>{t('kpi.margin_percentage')}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputs.marginPercentage || ''}
                      onChange={(e) =>
                        updateInput('marginPercentage', e.target.value)
                      }
                      type="text"
                      placeholder="ex: 30"
                      className="flex-1"
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'marginPercentage',
                          inputs.marginPercentage || '',
                          (value) => value >= 0 && value <= 100
                        )
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Prix de rétrocession*/}
              {isBuyingPriceFixedMode && (
                <div className="space-y-2">
                  <Label>
                    {t('kpi.buying_price')} ({t('kpi.excl_vat')}) (
                    {store === Store.FR ? Currency.EUR : Currency.USD})
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputs.buyingPrice || ''}
                      onChange={(e) =>
                        updateInput('buyingPrice', e.target.value)
                      }
                      type="text"
                      placeholder="ex: 24.99"
                      className="flex-1"
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'buyingPrice',
                          inputs.buyingPrice || '',
                          (value) => value >= 0
                        )
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Eco Tax */}
              <div className="space-y-2">
                <Label>
                  {t('kpi.eee_price')} (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.eeePrice || ''}
                    onChange={(e) => updateInput('eeePrice', e.target.value)}
                    type="text"
                    placeholder="ex: 0.05"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'eeePrice',
                        inputs.eeePrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Discounted Price Choose */}
              <div className="space-y-2">
                <Label>
                  {t('kpi.discounted_price')} ({vatLabel}) (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.discountedPrice || ''}
                    onChange={(e) =>
                      updateInput('discountedPrice', e.target.value)
                    }
                    type="text"
                    placeholder="ex: 24.99"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'discountedPrice',
                        inputs.discountedPrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {store === Store.FR && (
                <div className="space-y-2">
                  <Label>{t('kpi.vat')}</Label>
                  <div className="flex gap-2">
                    <Popover
                      open={vatRatePopoverOpen}
                      onOpenChange={setVatRatePopoverOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'flex-1 justify-between',
                            !inputs.vatRate && 'text-muted-foreground'
                          )}
                          type="button"
                          data-testid="bulk-vat-rate-trigger"
                        >
                          {inputs.vatRate
                            ? `${formatDecimal(Number(inputs.vatRate))}%`
                            : t('kpi.vat')}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="z-sidebar-dropdown w-[200px] p-0">
                        <Command>
                          <CommandList>
                            {FR_VAT_RATES.map((rate) => (
                              <CommandItem
                                key={rate}
                                value={String(rate)}
                                className="cursor-pointer"
                                onSelect={() => {
                                  updateInput('vatRate', String(rate));
                                  setVatRatePopoverOpen(false);
                                }}
                              >
                                {`${formatDecimal(rate)}%`}
                              </CommandItem>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'vatRate',
                          inputs.vatRate || '',
                          (value) =>
                            FR_VAT_RATES.some((rate) =>
                              isSameVatRate(rate, value)
                            )
                        )
                      }
                      size="sm"
                      disabled={!inputs.vatRate}
                      data-testid="bulk-vat-rate-apply"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              )}

              {/* VAT Margin */}
              {store === Store.FR && (
                <div className="space-y-2">
                  <Label>
                    {t('kpi.vat_ms')} ({t('kpi.vat_ms_long')})
                  </Label>
                  <div className="flex flex-row justify-between">
                    <Checkbox
                      defaultChecked={hasVatMargin || false}
                      onCheckedChange={() => updateHasVatMargin(!hasVatMargin)}
                      className=""
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog(
                          'hasVatMargin' as keyof DealItem,
                          hasVatMargin ? 'true' : 'false'
                        )
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section Logistique */}
          <div className="space-y-4">
            <h4 className="border-b border-gray-200 pb-1 text-sm font-medium text-gray-700">
              {t('components.bulk_edit_sidebar.logistics')}
            </h4>

            <div className="ml-2 space-y-4">
              {/* Frais de port à domicile */}
              <div className="space-y-2">
                <Label>
                  {t('kpi.shipping_price')} ({vatLabel}) (
                  {store === Store.FR ? Currency.EUR : Currency.USD})
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.shippingPrice || ''}
                    onChange={(e) =>
                      updateInput('shippingPrice', e.target.value)
                    }
                    type="text"
                    placeholder="ex: 2.90"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'shippingPrice',
                        inputs.shippingPrice || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>
              {/* Pays de fabrication */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">
                  {t('kpi.made_in')}
                </Label>
                <div className="flex gap-2">
                  <Popover
                    open={countryPopoverOpen}
                    onOpenChange={setCountryPopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        data-user-guide="Made in"
                        variant="outline"
                        className={cn(
                          'flex-1 justify-between',
                          !inputs.madeIn && 'text-muted-foreground'
                        )}
                        type="button"
                      >
                        {inputs.madeIn
                          ? country[inputs.madeIn] || inputs.madeIn
                          : t('kpi.made_in_select')}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="z-sidebar-dropdown w-[200px] p-0">
                      <Command>
                        <CommandInput
                          placeholder={t('kpi.made_in_research_placeholder')}
                        />
                        <CountryList
                          selectedCode={inputs.madeIn}
                          countryNames={country}
                          onSelect={(code) => {
                            updateInput('madeIn', code);
                            setCountryPopoverOpen(false);
                          }}
                        />
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    onClick={() =>
                      openConfirmDialog('madeIn', inputs.madeIn || '')
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>

              {/* Stock */}
              <div className="space-y-2">
                <Label>{t('kpi.stock')}</Label>
                <div className="flex gap-2">
                  <Input
                    value={inputs.stockQuantity || ''}
                    onChange={(e) =>
                      updateInput('stockQuantity', e.target.value)
                    }
                    type="text"
                    placeholder="ex: 100"
                    className="flex-1"
                  />
                  <Button
                    onClick={() =>
                      openConfirmDialog(
                        'stockQuantity',
                        inputs.stockQuantity || '',
                        (value) => value >= 0
                      )
                    }
                    size="sm"
                  >
                    {t('general.modify')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Section Développeur (developer-only) */}
          {user?.isDeveloper && (
            <div className="space-y-4">
              <h4 className="border-b border-gray-200 pb-1 text-sm font-medium text-gray-700">
                {t('components.bulk_edit_sidebar.developer')}
              </h4>

              <div className="ml-2 space-y-4">
                {/* GTIN */}
                <div className="space-y-2">
                  <Label>{t('kpi.gtin')}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={inputs.gtin || ''}
                      onChange={(e) => updateInput('gtin', e.target.value)}
                      type="text"
                      placeholder="ex: 1234567890123"
                      className="flex-1"
                    />
                    <Button
                      onClick={() =>
                        openConfirmDialog('gtin', inputs.gtin || '')
                      }
                      size="sm"
                    >
                      {t('general.modify')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Sidebar>

      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          !open && setConfirmDialog({ open: false, field: null, value: '' })
        }
      >
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {hasFieldLabel &&
                `${t('components.bulk_edit_sidebar.confirm_modify_base')} ${t(confirmDialogFieldLabels[confirmDialog.field!])} ?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {hasFieldLabel &&
                (confirmDialog.value === '' ? (
                  <span className="mt-2 flex items-start gap-2 rounded-md bg-orange-50 p-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                    <span>
                      {t(
                        'components.bulk_edit_sidebar.confirm_edit_null_value',
                        {
                          field: t(
                            confirmDialogFieldLabels[confirmDialog.field!]
                          ),
                          count: selectedCount,
                        }
                      )}
                    </span>
                  </span>
                ) : (
                  t('components.bulk_edit_sidebar.confirm_dialog_description', {
                    count: selectedCount,
                  })
                ))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedEdit}
              data-user-guide="Confirm Bulk"
            >
              {t('general.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={variantConfirmOpen}
        onOpenChange={(open) => !isAddingVariant && setVariantConfirmOpen(open)}
      >
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                inputs.variantOptionValue?.trim()
                  ? 'components.bulk_edit_sidebar.confirm_variant_option_title'
                  : 'components.bulk_edit_sidebar.confirm_variant_option_delete_title'
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                inputs.variantOptionValue?.trim()
                  ? 'components.bulk_edit_sidebar.confirm_variant_option_description'
                  : 'components.bulk_edit_sidebar.confirm_variant_option_delete_description',
                {
                  name: inputs.variantOptionName?.trim(),
                  value: inputs.variantOptionValue?.trim(),
                  count: selectedCount,
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isAddingVariant}>
              {t('general.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmedVariantOption();
              }}
              disabled={isAddingVariant}
              data-testid="confirm-bulk-variant-option"
            >
              {t('general.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showShippingPolicyModal && (
        <ShippingPolicyDialog
          open
          onOpenChange={setShowShippingPolicyModal}
          dealId={dealId}
          onConfirm={closeAndResetSelection}
          mode={ShippingMode.EditBulk}
        />
      )}
    </>
  );
};

export default BulkEditSidebar;
